import { ComponentCustomProperties } from 'vue';
import { i18n } from '@/common/locale';
import { showErrorMsg, showInfoMsg } from '../commonUtils';

/**
 * @fileoverview 클립보드 복사 유틸리티
 *
 * 텍스트를 클립보드에 복사하는 함수들을 제공합니다.
 * 최신 Clipboard API를 우선 사용하며, 지원하지 않는 환경에서는 fallback을 사용합니다.
 */

/**
 * DOM 이벤트에서 특정 클래스명을 가진 요소의 텍스트를 클립보드에 복사
 *
 * 이벤트 타겟의 부모 노드를 최대 3단계까지 탐색하여 지정된 클래스명을 가진 요소를 찾아
 * 해당 요소의 텍스트를 복사합니다. (레거시 함수 - execCommand 사용)
 *
 * @param e - DOM 이벤트 객체
 * @param ctx - Vue 컴포넌트 컨텍스트 (메시지 표시용)
 * @param className - 복사할 텍스트를 포함한 요소의 클래스명
 *
 * @deprecated execCommand 기반으로 더 이상 사용하지 않음. copyTextToClipboard 사용 권장
 */
const copyContext = (e, ctx, className) => {
  let context = e.target.parentNode;
  let wrapper;
  for (let i = 0; i < 3; i++) {
    // 최대 3번까지 부모 노드를 찾아감
    [wrapper] = context.getElementsByClassName(className);

    if (wrapper) {
      break;
    }

    context = context.parentNode;
  }

  if (!wrapper) {
    console.log('not found: copy wrapper element');
    return;
  }

  const tempElem = document.createElement('textarea');
  tempElem.value = wrapper.textContent.trim();
  context.appendChild(tempElem);
  tempElem.select();
  document.execCommand('copy');
  context.removeChild(tempElem);
  showInfoMsg(ctx, i18n.global.t('NOTI.UI.COPY'));
};

/**
 * 텍스트를 클립보드에 복사 (비동기)
 *
 * 최신 Clipboard API를 우선 사용하며, 지원하지 않는 환경에서는
 * execCommand를 fallback으로 사용합니다.
 *
 * - Clipboard API는 HTTPS 또는 localhost 환경에서만 동작합니다
 * - 제어 문자와 null 바이트는 자동으로 필터링됩니다
 * - 복사 성공/실패 시 사용자에게 메시지를 표시합니다
 *
 * @param ctx - Vue 컴포넌트 컨텍스트 (메시지 표시용, null 가능)
 * @param text - 복사할 텍스트 (문자열 또는 { format: string, raw: string } 객체)
 *
 * @example
 * // 문자열 복사
 * await copyTextToClipboard(ctx, 'Hello World');
 *
 * // 포맷된 텍스트 복사
 * await copyTextToClipboard(ctx, { format: 'Hello\nWorld', raw: 'Hello World' });
 *
 * @see https://web.dev/async-clipboard/#copy_writing_text_to_the_clipboard
 */
const copyTextToClipboard = async (
  ctx: (ComponentCustomProperties & Record<string, any>) | null,
  text: { format: string; raw: string } | string,
) => {
  let realText: string = '';
  if (typeof text !== 'string' && text?.format) {
    realText = text.format;
  } else {
    realText = text as string;
  }

  // 제어 문자와 null 바이트 필터링 (ASCII 코드 32 미만의 문자들)
  /* eslint-disable */
  realText = realText.replace(/[\x00-\x09\x0B\x0C\x0E-\x1F\x7F]/g, '');

  try {
    if (navigator.clipboard !== undefined) {
      await navigator.clipboard.writeText(realText);
    } else {
      const tempElem = document.createElement('textarea');
      tempElem.value = realText;
      document.body.appendChild(tempElem);
      tempElem.select();
      document.execCommand('copy');
      document.body.removeChild(tempElem);
    }

    if (ctx) {
      showInfoMsg(ctx, i18n.global.t('NOTI.UI.COPY'));
    }
  } catch (error) {
    console.log(`Error in copying text: ${error}`);

    if (ctx) {
      showErrorMsg(ctx, i18n.global.t('NOTI.UI.CANNOT_COPY'));
    }
  }
};

export { copyContext, copyTextToClipboard };
